import {
  estUnControle,
  NOM_FICHIER_MAX,
  PASTILLE_LARGEUR_MIN_MM_DEFAUT,
  type Rapport,
  type Reglages,
} from "./contract";

/**
 * Les textes du module Silhouettes, en fonctions pures : ce que la page ecrit a partir du
 * nom d'un fichier, des messages de l'outil et de son rapport (maquette
 * `Silhouettes.dc.html`, plan du 21 septembre 2026, issue #145).
 *
 * L'outil ecrit en ASCII, sans accents, et ses messages citent ses options de ligne de
 * commande (« --sol oui »). La console les traduit en reglages de la page, et le reglage
 * cite devient un lien qui l'applique. La traduction vit ici et pas dans `service.py` :
 * c'est la page qui sait quels reglages elle montre, et l'outil garde ses messages.
 */

/** Espace fine insecable : le separateur des milliers en francais (« 1 510 »). */
const ESPACE_FINE = "\u202f";

/**
 * Un nombre a la francaise : virgule decimale, espace fine pour les milliers, et
 * `decimales` chiffres apres la virgule **au plus** (`75,18` a une decimale donne `75,2`,
 * `4,0` donne `4`).
 */
export function nombreFr(valeur: number, decimales = 0): string {
  const arrondi = Number(valeur.toFixed(decimales));
  const signe = arrondi < 0 ? "-" : "";
  const [entier = "0", fraction] = Math.abs(arrondi).toString().split(".");
  const groupes = entier.replace(/\B(?=(\d{3})+(?!\d))/g, ESPACE_FINE);
  return `${signe}${groupes}${fraction ? `,${fraction}` : ""}`;
}

/** « blanc », « blanc et orange », « blanc, orange et bleu » : une liste a la francaise. */
export function enumeration(elements: readonly string[]): string {
  const dernier = elements.at(-1);
  if (dernier === undefined) return "";
  return elements.length === 1 ? dernier : `${elements.slice(0, -1).join(", ")} et ${dernier}`;
}

/**
 * La largeur sous laquelle un trait de couleur ne sort pas (#397) : celle que l'outil publie dans
 * son rapport, 0,8 mm a defaut (`products` pas encore deploye, ou couleurs non gardees : `null`).
 */
export function largeurMinDesPastilles(rapport: Rapport | null): number {
  return rapport?.pastille_largeur_min_mm ?? PASTILLE_LARGEUR_MIN_MM_DEFAUT;
}

/**
 * Le nom des fichiers, tire du nom de l'image : sans l'extension, en minuscules sans
 * accents, chaque suite de caracteres hors `[a-z0-9]` changee en un tiret, sans tiret au
 * bord, 40 caracteres au plus. `defaut` quand il ne reste rien : `silhouette` ici, `cadre`
 * pour le module Cadres (#167), qui reprend cette fonction au lieu de la recopier.
 */
export function nomDepuisFichier(nomFichier: string, defaut = "silhouette"): string {
  const sansExtension = nomFichier.replace(/\.[^.]*$/, "");
  const nom = sansExtension
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/, "");
  return nom === "" ? defaut : nom;
}

/** Un morceau de message : du texte, ou un reglage de la page que le texte cite. */
export type Segment = { texte: string; reglage?: Partial<Reglages> };

type Remplacement = {
  motif: string;
  segments: (option: string) => Segment[];
};

/**
 * Les options de la ligne de commande et ce qu'elles deviennent, **dans cet ordre** : a une
 * meme position, la premiere qui correspond gagne (`--sol-hauteur` avant `--sol`,
 * « Sans --sol non, » avant `--sol non`).
 */
const REMPLACEMENTS: readonly Remplacement[] = [
  {
    motif: "Sans --sol non,",
    segments: () => [
      { texte: "Avec " },
      { texte: "Ligne de sol : Auto", reglage: { sol: "auto" } },
      { texte: "," },
    ],
  },
  { motif: "--sol-hauteur", segments: () => [{ texte: "hauteur de la ligne de sol" }] },
  {
    motif: "--sol oui",
    segments: () => [{ texte: "Ligne de sol : Oui", reglage: { sol: "oui" } }],
  },
  {
    motif: "--sol non",
    segments: () => [{ texte: "Ligne de sol : Non", reglage: { sol: "non" } }],
  },
  { motif: "--sol(?![a-z-])", segments: () => [{ texte: "Ligne de sol" }] },
  {
    motif: "--mode epaissir",
    segments: () => [{ texte: "Traits fins : Épaissir", reglage: { mode: "epaissir" } }],
  },
  { motif: "--mode(?![a-z-])", segments: () => [{ texte: "Traits fins" }] },
  // La hauteur que l'outil propose quand la piece n'entre pas dans le serre-livre (#166) :
  // « --hauteur 81 » devient un lien qui l'applique, avant la regle du `--hauteur` seul.
  // Le nombre peut etre decimal, et il s'ecrit avec ses decimales a lui : la valeur montree
  // est celle qui part, jamais un arrondi.
  {
    motif: "--hauteur \\d+(?:\\.\\d+)?(?!\\d)",
    segments: (option) => {
      const ecrit = option.slice("--hauteur ".length);
      const decimales = ecrit.split(".")[1]?.length ?? 0;
      return [
        {
          texte: `Hauteur : ${nombreFr(Number(ecrit), decimales)} mm`,
          reglage: { hauteur_mm: Number(ecrit) },
        },
      ];
    },
  },
  { motif: "--hauteur(?![a-z-])", segments: () => [{ texte: "Hauteur" }] },
  { motif: "--ilots garder", segments: () => [{ texte: "îlots : garder" }] },
  // Les traits de couleur trop fins pour sortir (#397) : « --epaissir-couleur blanc » devient un lien
  // qui coche « blanc » dans la ligne « Traits de couleur ». Le nom est celui du rapport (minuscules,
  // chiffres et tirets) ; l'option seule, sans nom derriere, n'est que le nom du reglage.
  {
    motif: "--epaissir-couleur [a-z0-9-]{1,40}(?![a-z0-9-])",
    segments: (option) => {
      const nom = option.slice("--epaissir-couleur ".length);
      return [
        { texte: `Traits de couleur : épaissir ${nom}`, reglage: { couleurs_epaissies: [nom] } },
      ];
    },
  },
  { motif: "--epaissir-couleur(?![a-z-])", segments: () => [{ texte: "Traits de couleur" }] },
  // « --couleurs » garde les couleurs de l'image : la page l'appelle « Couleurs ». L'outil ne le cite
  // que pour dire « --epaissir-couleur blanc est sans effet : les couleurs de l'image ne sont pas
  // gardees », ce que la console ne provoque pas (elle n'envoie pas la cle sans couleurs gardees).
  { motif: "--couleurs(?![a-z-])", segments: () => [{ texte: "Couleurs" }] },
  // Toute autre option de la ligne de commande (`--pont-max`, `--largeur-min`, `--seuil`...) :
  // son nom seul, sans lien, la page n'ayant pas de reglage pour elle.
  { motif: "--[a-z][a-z-]*", segments: (option) => [{ texte: option.slice(2) }] },
];

/**
 * Une option n'est reconnue que hors d'un mot : en debut de texte, apres une espace ou apres
 * une parenthese (« ajoutee (--sol non pour s'en passer) », la ligne Sol du rapport). Collee
 * a une lettre, un chiffre ou un tiret, ce n'en est pas une : dans « goku--final.png »,
 * `--final` appartient au nom du fichier, qui doit rester entier.
 */
const OPTIONS = new RegExp(
  `(?<![\\w-])(?:${REMPLACEMENTS.map((r) => `(${r.motif})`).join("|")})`,
  "g",
);

/**
 * Les nombres decimaux de l'outil passent a la virgule : `4.4 mm` donne `4,4 mm`. Seulement un
 * nombre isole : « 10.42.13 » ou « v1.2 » ne bougent pas. Et deux decimaux que l'outil separe
 * par une virgule le sont par un point-virgule, sans quoi « 11,9, 16,0 » ne se lirait plus.
 * Le module Cadres s'en sert aussi pour les messages de sa chaine (#167).
 */
export function virgule(texte: string): string {
  return texte
    .replace(/(?<![\w.])(\d+\.\d+), (?=\d+\.\d+(?![\d.]))/g, "$1 ; ")
    .replace(/(?<![\w.])(\d+)\.(\d+)(?![\d.])/g, "$1,$2");
}

/** Deux morceaux de texte simple qui se suivent n'en font qu'un. */
function fusionner(segments: Segment[]): Segment[] {
  const fusionnes: Segment[] = [];
  for (const segment of segments) {
    const precedent = fusionnes.at(-1);
    if (segment.texte === "") continue;
    if (precedent && !precedent.reglage && !segment.reglage) {
      precedent.texte += segment.texte;
    } else {
      fusionnes.push({ ...segment });
    }
  }
  return fusionnes;
}

/**
 * Un message de l'outil, decoupe en segments : les options de la ligne de commande
 * deviennent des reglages de la page, la premiere lettre passe en majuscule et les nombres
 * decimaux a la virgule. Le reste du texte n'est pas reecrit : il reste sans accents.
 */
export function traduire(message: string): Segment[] {
  const segments: Segment[] = [];
  let depuis = 0;
  for (const trouve of message.matchAll(OPTIONS)) {
    const index = trouve.index ?? 0;
    segments.push({ texte: virgule(message.slice(depuis, index)) });
    const rang = trouve.slice(1).findIndex((groupe) => groupe !== undefined);
    const remplacement = REMPLACEMENTS[rang];
    segments.push(...(remplacement ? remplacement.segments(trouve[0]) : [{ texte: trouve[0] }]));
    depuis = index + trouve[0].length;
  }
  segments.push({ texte: virgule(message.slice(depuis)) });
  const resultat = fusionner(segments);
  const premier = resultat[0];
  if (premier && !premier.reglage) {
    premier.texte = premier.texte.charAt(0).toUpperCase() + premier.texte.slice(1);
  }
  return resultat;
}

/** Le texte de la maquette quand l'outil ne trouve rien a decouper dans l'image. */
export const AUCUN_SUJET =
  "Aucun sujet trouvé dans l'image. Essaie une image détourée (fond transparent), ou un dessin net sur un fond uni.";

/** L'image en tout dernier, et seulement si le message revient a toutes les hauteurs. */
const MAILLAGE_FIN =
  "Si ça revient à toutes les hauteurs, l'image est peut-être trop chargée : un dessin net sur un fond uni marche mieux.";

/**
 * Le repli, si ce refus arrivait sans que la page garde les couleurs. L'outil ne sait pas
 * l'emettre dans cet etat (`decouper_en_couleurs` n'est appele qu'avec une palette), mais un
 * texte vide serait pire qu'un texte general.
 */
export const MAILLAGE_OUVERT =
  "Le rendu s'est arrêté à cette taille : une forme n'a pas pu être fermée en volume. Essaie une autre hauteur. " +
  MAILLAGE_FIN;

/**
 * Le vrai cas, celui que Nicolas a vu le 25 septembre 2026 sur un dessin parfaitement net
 * (issue #186). Le moteur 3D disait « Not all meshes are volumes! », et la console traduisait
 * par « l'image est sans doute trop bruitee ». Deux mensonges dans une seule phrase :
 *
 * - **la cause n'est pas l'image**, elle etait geometrique (deux poches de couleur separees
 *   de 3e-7 mm, ecrasees par la conversion en float32 de trimesh) ;
 * - **ce n'est pas la piece qui est ouverte.** Ce 400 ne peut sortir que des booleens de
 *   `decouper_en_couleurs`, et celui-ci commence par refuser un solide qui ne serait pas un
 *   volume, avec son propre message. Quand ce refus-ci arrive, la piece est fermee : ce sont
 *   les pastilles de couleur, ou leur union, qui ne le sont pas.
 *
 * D'ou l'ordre : ce qui a echoue (les couleurs), puis le seul remede sur, offert en lien comme
 * le mode fidele l'est plus bas. « Une seule couleur » est le mot de l'outil pour ce resultat,
 * celui de son avertissement quand il renonce proprement aux couleurs (`chaine.py`). La hauteur
 * ne vient qu'apres, parce que la mesure de l'issue la contredit souvent (36 hauteurs essayees,
 * 36 en defaut sur l'image de Nicolas), et l'image en dernier.
 */
function maillageOuvert(couleursGardees: boolean): Segment[] {
  if (!couleursGardees) return [{ texte: MAILLAGE_OUVERT }];
  return [
    {
      texte:
        "Les couleurs n'ont pas pu être séparées à cette taille. Rends la pièce en une seule couleur en passant en ",
    },
    { texte: "Couleurs : ne pas garder", reglage: { couleurs: null } },
    { texte: `, ou essaie une autre hauteur. ${MAILLAGE_FIN}` },
  ];
}

/** Le refus du mode epaissir quand toute la silhouette est plus fine que la largeur minimale. */
const TROP_FINE = /^toute la silhouette est plus fine que .* --largeur-min\.?$/;

/**
 * Un refus de l'outil (400) : « aucun sujet » et le maillage ouvert ont leur texte, le reste
 * est traduit. Une silhouette trop fine pour le mode epaissir gagne un lien vers le mode
 * fidele, la vraie issue dans la page : l'outil, lui, ne cite que ses options. `couleursGardees`
 * est l'etat de la carte au moment du refus, pour ne proposer que des sorties atteignables.
 */
export function traduireErreur(message: string, couleursGardees = false): Segment[] {
  if (message.includes("aucun sujet trouve")) return [{ texte: AUCUN_SUJET }];
  if (message.includes("Not all meshes are volumes")) return maillageOuvert(couleursGardees);
  if (TROP_FINE.test(message)) {
    return [
      ...traduire(message.replace(/\.$/, "")),
      { texte: ", ou passer en " },
      { texte: "Traits fins : Fidèle", reglage: { mode: "fidele" } },
      { texte: "." },
    ];
  }
  return traduire(message);
}

/**
 * Les chiffres qui se lisent d'un coup d'oeil, sous l'apercu. Avec une ligne de sol, c'est sa
 * longueur qui compte : la barre la porte sur toute sa longueur, et la largeur d'appui ne dit
 * plus rien des personnages (commentaire de `chaine.py`, apres le calcul du contact).
 */
export function puces(rapport: Rapport): string[] {
  return lesPuces(rapport, false);
}

/** Les memes, raccourcies comme la maquette du telephone : « 18,5 g », « appui 12 mm ». */
export function pucesCourtes(rapport: Rapport): string[] {
  return lesPuces(rapport, true);
}

function lesPuces(rapport: Rapport, court: boolean): string[] {
  const resultat = [
    `${nombreFr(rapport.hauteur_hors_tout_mm, 1)} x ${nombreFr(rapport.largeur_mm, 1)} x ${nombreFr(rapport.epaisseur_mm, 1)} mm`,
    `${nombreFr(rapport.masse_pla_g, 1)} g${court ? "" : " de PLA"}`,
  ];
  if (rapport.ligne_de_sol_mm !== null) {
    resultat.push(`ligne de sol de ${nombreFr(rapport.ligne_de_sol_mm, 1)} mm`);
    // L'outil a releve la ligne tout seul pour attraper un second appui (#175) : c'est ce qui
    // explique une ligne plus haute que d'habitude, et rien n'est dit quand la hauteur est
    // celle par defaut ou celle que Nicolas a imposee. Le champ manque tant que `products`
    // n'est pas deploye : pas de champ, pas de puce, comme pour le serre-livre.
    if (rapport.sol_hauteur_relevee === true && rapport.sol_hauteur_mm !== null) {
      resultat.push(`sol relevé à ${nombreFr(rapport.sol_hauteur_mm, 1)} mm`);
    }
  } else if (rapport.contact_barre_mm !== null) {
    const appui = nombreFr(rapport.contact_barre_mm, 1);
    resultat.push(court ? `appui ${appui} mm` : `appui de ${appui} mm sur la barre`);
  }
  if (rapport.etanche && rapport.corps === 1) {
    resultat.push(court ? "étanche" : "étanche, un seul corps");
  } else {
    if (!rapport.etanche) resultat.push("non étanche");
    if (rapport.corps !== 1) resultat.push(`${rapport.corps} morceaux`);
  }
  // Le serre-livre, en dernier (#166). Le champ manque tant que `products` n'est pas deploye,
  // et sans attache : rien n'est dit plutot qu'une puce qui affirmerait le contraire. Le
  // telephone garde la puce, raccourcie : « trop large pour le serre-livre » est ce qui decide
  // d'un rendu, et une puce de plus est moins couteuse qu'une information perdue.
  if (rapport.serre_livre === true) {
    const cote = coteVersLeMur(rapport);
    if (cote === null) resultat.push("entre dans le serre-livre");
    else if (court) resultat.push(`serre-livre : côté ${cote} au mur`);
    else resultat.push(`entre dans le serre-livre, côté ${cote} vers le mur`);
  }
  if (rapport.serre_livre === false) resultat.push("trop large pour le serre-livre");
  return resultat;
}

/**
 * De quel cote tourner la piece vers le mur du serre-livre : **c'est l'outil qui le dit**
 * (`serre_livre_cote`), la console ne le deduit pas des debords (#166). Elle n'a pas le
 * degagement du mur (6,75 mm) et ne peut donc pas savoir si les deux sens passent : des
 * debords de 6,6 et 6,9 mm se ressemblent, et pourtant un seul sens entre. **Gauche et droite
 * sont celles de l'apercu affiche**, pas celles de la piece retournee face coloree contre le
 * plateau a l'impression. `null` quand il n'y a rien a dire : les deux sens entrent, ou le
 * champ manque (`products` pas encore deploye, profil sans ces cotes).
 */
function coteVersLeMur(rapport: Rapport): "gauche" | "droit" | null {
  // L'outil nomme un cote (« droite »), la puce qualifie celui de la piece (« cote droit »).
  if (rapport.serre_livre_cote === "gauche") return "gauche";
  if (rapport.serre_livre_cote === "droite") return "droit";
  return null;
}

export type Filament = { numero: number; nom: string; hexa: string; detail: string };

/**
 * Les filaments, dans l'ordre du rapport : la base d'abord, puis les pastilles. C'est
 * l'ordre des extrudeurs du 3MF, donc celui des numeros dans Bambu Studio.
 */
export function filaments(rapport: Rapport, attache: boolean): Filament[] {
  return (rapport.couleurs ?? []).map((couleur, index) => ({
    numero: index + 1,
    nom: couleur.nom,
    hexa: couleur.hexa,
    detail:
      couleur.role === "base"
        ? attache
          ? "base, barre et contour"
          : "base et contour"
        : `${nombreFr(couleur.surface_mm2 ?? 0)} mm²`,
  }));
}

/** Le titre de la liste des filaments ; `court`, celui du telephone (« 4 filaments »). */
export function titreFilaments(rapport: Rapport, court = false): string {
  const nombre = rapport.couleurs?.length ?? 0;
  if (nombre <= 1) return "Une seule couleur : un seul filament.";
  return court ? `${nombre} filaments` : `${nombre} filaments, dans l'ordre de Bambu Studio`;
}

/** `1 zone fermée remplie`, `2 zones fermées remplies` : le mot suit le compte. */
function accorder(nombre: number, singulier: string, pluriel: string): string {
  return `${nombre} ${nombre > 1 ? pluriel : singulier}`;
}

/**
 * « Ce que le rendu a fait », dans l'ordre, seulement les lignes qui s'appliquent. Les
 * couleurs citees sont celles de la legende de l'apercu (`silhouette/apercu.py`).
 */
export function bilan(rapport: Rapport): string[] {
  const lignes: string[] = [];
  const ajoute = nombreFr(rapport.matiere_ajoutee_mm2, 1);
  const largeurMin = nombreFr(rapport.largeur_min_mm, 1);
  if (rapport.mode === "fidele") {
    lignes.push(
      rapport.matiere_ajoutee_mm2 < 0.5
        ? "Contour du dessin gardé tel quel : rien d'ajouté."
        : `Contour du dessin gardé ; ${ajoute} mm² ajoutés (en orange dans l'aperçu).`,
    );
    if (rapport.part_trop_fine_pct > 0) {
      lignes.push(
        `${nombreFr(rapport.part_trop_fine_pct, 2)} % de la surface plus fin que ${largeurMin} mm (en rose), gardé.`,
      );
    }
  } else {
    lignes.push(
      `Traits fins épaissis jusqu'à ${largeurMin} mm : ${ajoute} mm² ajoutés (en orange dans l'aperçu).`,
    );
  }
  if (rapport.zones_remplies > 0) {
    const plusGrande = nombreFr(rapport.zones_remplies_mm2[0] ?? 0);
    lignes.push(
      `${accorder(rapport.zones_remplies, "zone fermée remplie", "zones fermées remplies")}, la plus grande de ${plusGrande} mm² (en jaune).`,
    );
  }
  if (rapport.ilots_rattaches > 0) {
    lignes.push(
      `${accorder(rapport.ilots_rattaches, "morceau rattaché", "morceaux rattachés")} par un pont (en bleu).`,
    );
  }
  if (rapport.trous_bouches > 0) {
    lignes.push(`${accorder(rapport.trous_bouches, "petit trou bouché", "petits trous bouchés")}.`);
  }
  if (rapport.ligne_de_sol_mm !== null) {
    lignes.push(
      `Ligne de sol de ${nombreFr(rapport.ligne_de_sol_mm, 1)} mm ajoutée sous les pieds (en violet).`,
    );
  }
  // Les traits de couleur epaissis (#397), puisque rien d'autre ne le dit : leur avertissement « ne
  // sortent pas » disparait avec le probleme. Ceux qui sont retires ont le leur, avec le lien qui les
  // epaissit : le redire ici ferait doublon. Le champ manque tant que `products` n'est pas deploye.
  // Cette ligne dit ce qu'on a demande a l'outil, pas ce qu'il a obtenu : des traits restes fins, ou la
  // paroi que les traits epaissis laissent a la base, il le dit dans un avertissement (du texte, aucun
  // champ du rapport), et la console ne lit pas ce texte (decision du 12 octobre 2026, #397).
  const epaissis = (rapport.traits_de_couleur ?? [])
    .filter((trait) => trait.traitement === "epaissis")
    .map((trait) => trait.nom);
  if (epaissis.length > 0) {
    lignes.push(
      `Traits de couleur épaissis jusqu'à ${nombreFr(largeurMinDesPastilles(rapport), 2)} mm : ${enumeration(epaissis)}.`,
    );
  }
  // Une seule couleur : pas de face coloree, rien a dire du sens de la piece.
  if (rapport.couleurs !== null && rapport.couleurs.length > 1) {
    lignes.push("Pièce retournée : la face colorée s'imprime contre le plateau.");
  }
  return lignes;
}

/**
 * Le nom du fichier tel qu'il part : sans caractere de controle (la regle de `products`), et
 * coupe a 200 unites sans casser un caractere en deux. Un nom trop long faisait un 400 de
 * validation en anglais, presente comme un refus de l'outil (relecture de #145).
 */
export function nettoyerNomFichier(nom: string): string {
  let propre = "";
  for (const car of nom) {
    if (estUnControle(car.codePointAt(0) ?? 0)) continue;
    if (propre.length + car.length > NOM_FICHIER_MAX) break;
    propre += car;
  }
  return propre;
}

/** `170 Ko` sous le mega-octet, `1,4 Mo` au-dela (1 024 octets le kilo, comme le systeme). */
export function taille(octets: number): string {
  if (octets < 1024 * 1024) return `${nombreFr(octets / 1024)} Ko`;
  return `${nombreFr(octets / (1024 * 1024), 1)} Mo`;
}

/**
 * La ligne sous le nom du fichier : `580 x 959 px · 170 Ko`, puis ce que l'outil a vu du
 * fond, une fois le premier rendu fait (`origine` vient du rapport).
 */
export function decrireImage(
  largeur: number,
  hauteur: number,
  octets: number,
  origine?: string,
): string {
  const base = `${largeur} x ${hauteur} px · ${taille(octets)}`;
  if (origine === undefined) return base;
  if (origine === "canal alpha") return `${base} · détourée (fond transparent)`;
  return `${base} · sur fond uni`;
}
